import { useState, useEffect } from "react"
import { ArrowRight } from "lucide-react"
import { Button } from "~/components/ui/button"
import { SectionReveal } from "~/components/company/SectionReveal"

export interface InquiryOption {
  value: string
  label: string
}

interface ContactSectionProps {
  title?: string
  description?: string
  inquiryPrompt?: string
  inquiryOptions?: InquiryOption[]
  service?: string
}

const DEFAULT_INQUIRY_OPTIONS: InquiryOption[] = [
  { value: "retainer", label: "$8,000-$12,000/month retainer - full service" },
  { value: "software", label: "Software development project" },
  { value: "consulting", label: "Business strategy consulting" },
  { value: "offshore", label: "Offshore talent hiring" },
  { value: "sop", label: "Workflow mapping & SOP development" },
  { value: "other", label: "Other - please contact me to discuss" },
]

export function ContactSection({
  title = "Ready to Get 2x-6x Value on Your Investment?",
  description = "Book a discovery call and see how $8,000-$12,000/month can transform your business with a dedicated team.",
  inquiryPrompt = "What are you most interested in?",
  inquiryOptions = DEFAULT_INQUIRY_OPTIONS,
  service,
}: ContactSectionProps) {
  const [showThankYou, setShowThankYou] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const form = e.target as HTMLFormElement
    const formData = new FormData(form)

    try {
      const response = await fetch("https://formspree.io/f/xkgbdnge", {
        method: "POST",
        body: formData,
        headers: { Accept: "application/json" },
      })

      if (response.ok) {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({ event: 'lead_form_submit' })
        setShowThankYou(true)
        form.reset()
        setTimeout(() => setShowThankYou(false), 5000)
      } else {
        throw new Error("Form submission failed")
      }
    } catch (error) {
      console.error("Error submitting form:", error)
      alert("There was an error submitting the form. Please try again.")
    }
  }

  if (!mounted) return null

  return (
    <section id="contact" className="w-full py-12 md:py-24 lg:py-32 bg-primary">
      <div className="container px-8 md:px-12 lg:px-16">
        <SectionReveal className="flex flex-col items-center justify-center space-y-8 text-center text-white">
          <div className="space-y-2">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-5xl">{title}</h2>
            <p className="max-w-[900px] md:text-xl">{description}</p>
          </div>
          <div className="w-full max-w-md space-y-4">
            <p className="text-sm text-white/80">Responds within 15 minutes during US East business hours</p>
            <form className="space-y-4" action="https://formspree.io/f/xkgbdnge" method="POST" onSubmit={handleSubmit}>
              {service && <input type="hidden" name="service" value={service} />}
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white"
                required
              />
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white"
                required
              />
              <input
                type="text"
                name="company"
                placeholder="Company Name"
                className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white"
              />
              <input
                type="tel"
                name="phone"
                placeholder="Phone Number (Optional)"
                className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white"
              />
              <select
                name="inquiry"
                className="w-full px-4 py-3 rounded-lg bg-white border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-white"
                required
              >
                <option value="">{inquiryPrompt}</option>
                {inquiryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <Button type="submit" className="w-full !bg-white !text-brand hover:!bg-gray-100 py-3">
                Send Message
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>
            {showThankYou && (
              <div className="mt-4 p-4 bg-white/20 rounded-lg text-center">
                <p className="text-white font-medium">Thank you! John will reach out to you ASAP.</p>
              </div>
            )}
          </div>
        </SectionReveal>
      </div>
    </section>
  )
}
